import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Snackbar, Alert, CircularProgress, Grid, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, TextField, IconButton
} from '@mui/material';
import { FilterList as FilterListIcon, Download as DownloadIcon } from '@mui/icons-material';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip } from 'recharts';
import { Sidebar } from '../components/Sidebar';

declare global {
  interface ImportMeta {
    env: {
      VITE_API_BASE_URL: string;
    };
  }
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface User {
  id: string;
  username: string;
  role: string;
}

interface HistoryPageProps {
  user: User | null;
  onLogout: () => void;
}

export default function HistoryPage({ user, onLogout }: HistoryPageProps) {
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({ open: false, message: '', severity: 'success' });
  const [filterDialog, setFilterDialog] = useState({ open: false });
  const [filter, setFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/sheets/history`);
      const data = await response.json() as { history?: any[] };
      setHistory(data.history || []);
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to fetch history', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (ts: string) => ts ? new Date(ts).toLocaleString() : '-';
  const formatDuration = (startTime: string, endTime: string) => {
    if (!startTime || !endTime) return '-';
    const durationMs = new Date(endTime).getTime() - new Date(startTime).getTime();
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };
  const formatCost = (cost: number | undefined) => cost ? `Rp${cost.toLocaleString()}` : '-';

  // Filtered history
  const getFilteredHistory = () => {
    let filtered = history;
    if (dateRange.start) {
      filtered = filtered.filter(entry => new Date(entry.startTime) >= new Date(dateRange.start));
    }
    if (dateRange.end) {
      const filterEnd = new Date(dateRange.end);
      filterEnd.setHours(23, 59, 59, 999);
      filtered = filtered.filter(entry => new Date(entry.endTime || entry.startTime) <= filterEnd);
    }
    return filtered.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
  };
  const filteredHistory = getFilteredHistory();

  // Summary
  const totalRevenue = filteredHistory.reduce((sum, entry) => sum + (entry.cost || 0), 0);
  const totalSessions = filteredHistory.filter(entry => entry.endTime).length;

  // Chart data
  const revenueByDay: Record<string, number> = {};
  filteredHistory.forEach(entry => {
    if (entry.startTime && entry.endTime) {
      const startDate = new Date(entry.startTime);
      const day = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
      revenueByDay[day] = (revenueByDay[day] || 0) + (entry.cost || 0);
    }
  });
  const chartData = Object.entries(revenueByDay)
    .map(([date, revenue]) => ({ date, revenue }))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  // Download CSV/Excel
  const handleDownloadCSV = () => {
    if (filteredHistory.length === 0) {
      setSnackbar({ open: true, message: 'No data to download', severity: 'warning' });
      return;
    }
    setDownloading(true);
    try {
      const headers = ['Device', 'Start Time', 'End Time', 'Duration', 'Price/Hour', 'Cost'];
      const rows = filteredHistory.map(entry => [
        entry.customName || entry.deviceName || 'Unknown',
        formatTime(entry.startTime),
        formatTime(entry.endTime),
        formatDuration(entry.startTime, entry.endTime),
        entry.price ? `Rp${entry.price.toLocaleString()}` : '-',
        formatCost(entry.cost)
      ]);
      const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `rental_history_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setSnackbar({ open: true, message: 'Download success', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Download failed', severity: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadExcel = handleDownloadCSV; // For simplicity, use CSV for Excel too

  // UI
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f5f7' }}>
      {/* Sidebar */}
      {/* <Sidebar 
        user={user}
        currentPage="history"
        onNavigate={onNavigate}
        onLogout={onLogout}
      /> */}
      {/* Main Content */}
      <Box sx={{ flex: 1, p: 4 }}>
        {/* Header & Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" fontWeight={700} color="#3d36fa">
            Rental History
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button variant="outlined" startIcon={<FilterListIcon />} onClick={() => setFilterDialog({ open: true })} size="small">
              Filters
            </Button>
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownloadCSV} disabled={downloading || filteredHistory.length === 0} size="small" sx={{ bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}>
              Export to CSV
            </Button>
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownloadExcel} disabled={downloading || filteredHistory.length === 0} size="small" sx={{ bgcolor: 'info.main', '&:hover': { bgcolor: 'info.dark' } }}>
              Export to Excel
            </Button>
          </Box>
        </Box>
        {/* Summary & Chart */}
        <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 220, maxWidth: 320 }}>
            <Paper sx={{ p: 3, borderRadius: 3, bgcolor: '#6c63ff', color: 'white' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Total Sessions
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {totalSessions}
              </Typography>
            </Paper>
            <Paper sx={{ p: 3, borderRadius: 3, bgcolor: '#3ec46d', color: 'white' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Total Revenue
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Rp{totalRevenue.toLocaleString()}
              </Typography>
            </Paper>
          </Box>
          <Paper sx={{ flex: 2, minWidth: 320, p: 3, bgcolor: 'grey.50', color: 'text.secondary', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Revenue Per Day
            </Typography>
            {chartData.length === 0 ? (
              <Typography variant="body2">No data</Typography>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <XAxis dataKey="date" fontSize={12} tickFormatter={d => new Date(d).toLocaleDateString()} />
                  <YAxis fontSize={12} tickFormatter={v => `Rp${v.toLocaleString()}`} />
                  <RechartsTooltip formatter={v => `Rp${v.toLocaleString()}`} />
                  <Line type="monotone" dataKey="revenue" stroke="#4caf50" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Box>
        {/* History Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Device</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Start Time</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>End Time</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Price/Hour</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Cost</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredHistory.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No rental history found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredHistory.map((entry) => (
                    <TableRow 
                      key={entry.id} 
                      sx={{ '&:hover': { bgcolor: 'action.hover' }, transition: 'background-color 0.2s' }}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>{entry.customName || entry.deviceName}</TableCell>
                      <TableCell>{formatTime(entry.startTime)}</TableCell>
                      <TableCell>{formatTime(entry.endTime)}</TableCell>
                      <TableCell>{formatDuration(entry.startTime, entry.endTime)}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>{entry.price ? `Rp${entry.price.toLocaleString()}` : '-'}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'success.main' }}>{formatCost(entry.cost)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {/* Snackbar */}
        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
        {/* Filter Dialog */}
        <Dialog open={filterDialog.open} onClose={() => setFilterDialog({ open: false })} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon /> Filter History
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Date Range</Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField label="Start Date" type="date" value={dateRange.start} onChange={e => setDateRange({ ...dateRange, start: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} />
                  <TextField label="End Date" type="date" value={dateRange.end} onChange={e => setDateRange({ ...dateRange, end: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} />
                </Box>
                <Button variant="outlined" onClick={() => setDateRange({ start: '', end: '' })} size="small" sx={{ alignSelf: 'flex-start' }}>Clear Dates</Button>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFilterDialog({ open: false })}>Cancel</Button>
            <Button onClick={() => setFilterDialog({ open: false })} variant="contained">Apply Filters</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
} 